/**
 * BackOrderOffcanvas — the "mini back order" slide-out (parallel to the mini cart).
 * Opened from the header Back Order button. Shows the live back order with uncapped quantity
 * inputs; edits auto-persist. A footer link opens the full back-order page.
 */
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useBackOrder } from "../../hooks/useBackOrder";
import { useIsMobile } from "../../hooks/useIsMobile";
import { ProductQuantityCalculator } from "../../utils/ProductQuantityCalculator";
import { formatCurrency } from "../../utils/formatters";
import BackOrderItemRow from "./BackOrderItemRow";

const BackOrderOffcanvas = () => {
  const {
    backOrderItems,
    isLoading,
    isUpdating,
    setBackOrderQuantity,
    backOrderItemCount,
  } = useBackOrder();

  const isMobile = useIsMobile();
  const navigate = useNavigate();

  // Close the offcanvas (and its backdrop) before navigating, mirroring the mini cart.
  const handleNavigate = (path) => {
    const el = document.getElementById("backOrderOffcanvas");
    if (el) {
      const bsOffcanvas = window.bootstrap?.Offcanvas?.getInstance(el);
      if (bsOffcanvas) {
        bsOffcanvas.hide();
      } else {
        const closeBtn = el.querySelector('[data-bs-dismiss="offcanvas"]');
        if (closeBtn) {
          closeBtn.click();
        } else {
          el.classList.remove("show");
          const backdrop = document.querySelector(".offcanvas-backdrop");
          if (backdrop) backdrop.remove();
        }
      }
    }
    navigate(path);
  };

  const backOrderTotal = useMemo(
    () =>
      backOrderItems.reduce((sum, item) => {
        const calc = new ProductQuantityCalculator({
          id: item.productSkuVariantID,
          unitsPerCase: item.productSkuVariant?.unitsPerCase || 1,
          optSellByTypeID: item.productSkuVariant?.optSellByTypeID || 1,
          unitPrice: item.productSkuVariant?.unitPrice || item.unitPrice || 0,
          singleUnitFee: item.singleUnitFee || item.productSkuVariant?.singleUnitFee || 0,
        });
        return sum + (calc.calculatePrice(item.unitQuantity || 0)?.grandTotal || 0);
      }, 0),
    [backOrderItems],
  );

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="backOrderOffcanvas"
      aria-labelledby="backOrderOffcanvasLabel"
      style={{ width: "90%", maxWidth: "900px" }}
    >
      <div className="offcanvas-header border-bottom justify-content-between">
        <h5 className="offcanvas-title fw-bold" id="backOrderOffcanvasLabel">
          <i className="bi bi-clock-history me-2"></i>
          YOUR BACK ORDER ({backOrderItemCount})
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        ></button>
      </div>

      <div className="offcanvas-body p-0">
        <div className="px-3 pt-3">
          <p className="text-muted small">
            Items requested beyond current stock — we&apos;ll fulfill these as they&apos;re
            restocked. Changes save automatically.
          </p>

          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : backOrderItems.length > 0 ? (
            <div className="backorder-items-container border border-dark">
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <tbody>
                    {backOrderItems.map((item) => (
                      <BackOrderItemRow
                        key={item.id}
                        item={item}
                        onSetQuantity={setBackOrderQuantity}
                        isUpdating={isUpdating}
                        isMobile={isMobile}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-5">
              <i className="bi bi-clock-history display-4 text-muted d-block mb-3"></i>
              <h6 className="text-muted">No back orders yet</h6>
              <p className="small text-muted mb-3">
                When a product is out of stock (or you want more than is available), add all
                available stock to your cart and a &quot;Create Back Order&quot; option appears.
              </p>
              <button
                type="button"
                className="btn btn-dark"
                onClick={() => handleNavigate("/categories")}
              >
                Browse Products
              </button>
            </div>
          )}
        </div>
      </div>

      {backOrderItems.length > 0 && (
        <div className="border-top p-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="text-muted">Estimated back order value</span>
            <strong className="text-stash-accent fs-5">{formatCurrency(backOrderTotal)}</strong>
          </div>
          <button
            type="button"
            className="btn btn-dark w-100"
            onClick={() => handleNavigate("/back-orders")}
          >
            View Full Back Order
          </button>
        </div>
      )}

      <style>{`
        /* Desktop scroll region. Full-width offcanvas (<=768px) and the card layout (<=576px)
           are handled by the shared cart card styles in _cart.scss, scoped to #backOrderOffcanvas. */
        .backorder-items-container {
          max-height: 60vh;
          overflow-y: auto;
        }

        .backorder-items-container::-webkit-scrollbar {
          width: 6px;
        }

        .backorder-items-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .backorder-items-container::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .backorder-items-container::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
};

export default BackOrderOffcanvas;
